import React, { useState } from "react";
import { Mail, Sparkles } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { toast } from "sonner";

const STORAGE_KEY = "signapharma-visitor-email-v1";
const API = process.env.REACT_APP_BACKEND_URL;

export function getStoredVisitorEmail() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; }
  catch (_e) { return ""; }
}

/**
 * EmailGateModal — affichée dès l'ouverture d'un configurateur si l'utilisateur
 * n'a pas encore donné son email. Une fois validé, l'email est :
 *   - stocké en localStorage (plus jamais redemandé sur ce navigateur)
 *   - envoyé au backend → notification Resend vers info@signapharma.fr
 *
 * Pas d'option de fermeture — l'utilisateur doit donner son email pour
 * accéder au configurateur (modal d'entrée obligatoire).
 */
export default function EmailGateModal({ projectContext, onClose }) {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isValid) {
      toast.error(lang === "fr" ? "Email invalide" : "Invalid email");
      return;
    }
    setSubmitting(true);
    try {
      // Stocke d'abord localement (UX : ne bloque pas si l'API échoue)
      try { localStorage.setItem(STORAGE_KEY, email.trim()); } catch (_e) {}
      const res = await fetch(`${API}/api/contact/email-capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          project_name: projectContext || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(lang === "fr" ? "Bienvenue ! Accès au configurateur ouvert." : "Welcome! Configurator unlocked.");
      onClose?.();
    } catch (err) {
      // On laisse passer même si l'API échoue (email déjà stocké en local).
      toast.warning(lang === "fr" ? "Email enregistré localement (notification serveur indisponible)" : "Email saved locally (server notification unavailable)");
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="email-gate-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <form onSubmit={submit}
        className="relative w-full max-w-md bg-[#0a0a0a] border border-[#39FF14]/40 shadow-[0_24px_48px_-12px_rgba(57,255,20,0.25)] p-6 sm:p-8 space-y-5">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 border border-[#39FF14]/50 bg-[#39FF14]/10">
            <Sparkles size={18} className="text-[#39FF14]" />
          </span>
          <div>
            <div className="font-display text-base font-semibold uppercase tracking-[0.15em] text-white">
              {lang === "fr" ? "Bienvenue chez Signapharma" : "Welcome to Signapharma"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14] mt-1">
              {lang === "fr" ? "Accès au configurateur" : "Configurator access"}
            </div>
          </div>
        </div>

        <p className="font-body text-sm text-zinc-300 leading-relaxed">
          {lang === "fr"
            ? "Pour démarrer votre montage personnalisé, indiquez-nous simplement votre adresse email. Aucune création de compte requise — nous restons à votre disposition pour répondre à vos questions et finaliser votre devis."
            : "To start your custom design, please share your email. No account needed — we'll be available to answer your questions and finalize your quote."}
        </p>

        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 flex items-center gap-2">
            <Mail size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Votre email" : "Your email"}
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jean.dupont@pharmacie.ch"
            autoFocus
            required
            data-testid="email-gate-input"
            className="w-full bg-black border border-white/15 px-4 py-3 font-body text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14] focus:shadow-[0_0_18px_rgba(57,255,20,0.2)]"
          />
        </label>

        <button
          type="submit"
          disabled={!isValid || submitting}
          data-testid="email-gate-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 hover:bg-[#39FF14]/20 disabled:opacity-40 disabled:cursor-not-allowed font-display text-sm font-semibold uppercase tracking-[0.2em] transition-colors"
        >
          {submitting
            ? (lang === "fr" ? "Envoi…" : "Sending…")
            : (lang === "fr" ? "Accéder au configurateur" : "Unlock configurator")}
        </button>

        <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 text-center">
          {lang === "fr"
            ? "Vos données restent confidentielles — nous ne les partageons pas."
            : "Your data stays confidential — we never share it."}
        </p>
      </form>
    </div>
  );
}
